import React, { useEffect } from 'react';
import { ProjectFormValues } from '../ProjectFlow';
import * as projectConstants from '../constants';
import mascotHappy from '../../../../assets/svg/onboarding/mascot-happy.svg';
import editIcon from '../../../../assets/svg/landing/icon-pen.svg';
import heartRed from '../../../../assets/svg/heart-red.svg';
import coinWhite from '../../../../assets/svg/coin-white.svg';
import globeRed from '../../../../assets/images/project/globe-red.svg';
import usersRed from '../../../../assets/images/project/users-red.svg';

export const formatGenreName = (name: string): string => {
    return name.replace(/([A-Z])/g, ' $1').trim();
};

interface ProjectPreviewStepProps {
    formValues: ProjectFormValues;
    onEdit: (step: number) => void;
    onSubmit: () => void;
    isSubmitting: boolean;
    user?: any;
    acceptedFeeTerms: boolean;
    setAcceptedFeeTerms: (value: boolean) => void;
}

const ProjectPreviewStep: React.FC<ProjectPreviewStepProps> = ({
    formValues,
    onEdit,
    onSubmit,
    isSubmitting,
    user,
    acceptedFeeTerms,
    setAcceptedFeeTerms
}) => {
    useEffect(() => {
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'auto' });
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
        }, 100);
    }, []);
    const isBrand = user?.userType === 'BRAND';
    return (
        <div className="columns section page-add-project-container project-preview no-bottom-margin">
            <div className="column">
                <div className="preview-page-wrapper">
                    <div className="preview-header-section">
                        <div className="modal-progress-bar-container">
                            <div className="modal-progress-bar width-100" />
                        </div>
                        <div className="preview-progress-indicator">
                            2/2 Preview
                        </div>

                        <div className="preview-header-horizontal">
                            <div className="preview-header-left">
                                <h1 className="preview-header-title">
                                    Publish your project
                                </h1>
                                <p className="preview-header-subtitle">
                                    Review Or Change Your Project Information
                                </p>
                            </div>
                            <div className="preview-header-right">
                                <img
                                    src={mascotHappy}
                                    alt="Mascot"
                                    className="preview-mascot-image"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Content Card */}
                    <div className="preview-content-card">
                        <div className="preview-section-label">Project Preview</div>
                        <div className="project-title-section">
                            <h2 className="project-dynamic-title">
                                {formValues.title || 'Your Project'}
                            </h2>
                            <button
                                type="button"
                                className="project-edit-button-circle"
                                onClick={() => onEdit(2)}
                            >
                                <img src={editIcon} alt="Edit" />
                            </button>
                        </div>

                        {/* Project Description */}
                        <div className="preview-field mb-4">
                            <p className="preview-description">{formValues.description}</p>
                        </div>

                        {/* Project Images - 2x2 Grid */}
                        {formValues.medias && formValues.medias.length > 0 && (
                            <div className="preview-images-grid mb-4">
                                {formValues.medias.slice(0, 4).map((media, idx) => (
                                    <div key={media.id} className="preview-image-item">
                                        <img
                                            src={media.fileUrl}
                                            alt={`Project image ${idx + 1}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="preview-field mb-4">
                            <label className="preview-label">Type & Share With</label>
                            <div className="preview-row-fields">
                                <div className="preview-bordered-field">
                                    <div className="preview-value-with-icon">
                                        <img
                                            src={formValues.type === 'PASSION' ? heartRed : coinWhite}
                                            alt=""
                                            className="preview-icon-mono"
                                        />
                                        <span>{formValues.type === 'PASSION' ? 'Passion project' : 'Commissioned'}</span>
                                    </div>
                                </div>
                                <div className="preview-bordered-field">
                                    <div className="preview-value-with-icon">
                                        <img
                                            src={formValues.shareWith === 'PUBLIC' ? globeRed : usersRed}
                                            alt=""
                                            className="preview-icon-mono"
                                        />
                                        <span>{formValues.shareWith === 'PUBLIC' ? 'Public' : 'Matchhat community'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* I'm Looking For */}
                        {formValues.collaborations && formValues.collaborations.length > 0 && (
                            <div className="preview-field mb-4">
                                <label className="preview-label">I'm Looking For</label>
                                {formValues.collaborations.map((collab, idx) => {
                                    if (!collab.professions || collab.professions.length === 0 || !collab.professions[0]) return null;
                                    return (
                                        <div key={idx} className="preview-bordered-field mb-3" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'flex-start' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span style={{ fontWeight: 600, color: '#374151' }}>{collab.professions[0]}</span>
                                                <span style={{ color: '#6B7280', fontSize: '0.875rem' }}>
                                                    {projectConstants.collaboratorCompensationTypeOptions.find(c => c.value === collab.compensation)?.label ?? collab.compensation}
                                                </span>
                                            </div>
                                            {collab.additionalRequirements && (
                                                <span style={{ color: '#4B5563', fontSize: '0.875rem', marginTop: '0.25rem' }}>{collab.additionalRequirements}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* You're Looking For */}
                        {formValues.requiredSkills && formValues.requiredSkills.length > 0 && (
                            <div className="preview-field mb-4">
                                <label className="preview-label">You're Looking For</label>
                                <div className="preview-tags">
                                    {formValues.requiredSkills.map((skill, idx) => (
                                        <div key={idx} className="preview-tag-chip">
                                            <span className="tag-text">{skill}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Country */}
                        {formValues.country && (
                            <div className="preview-field mb-4">
                                <label className="preview-label">Country</label>
                                <div className="preview-bordered-field">
                                    <p className="preview-value mb-0">{formValues.country}</p>
                                </div>
                                {formValues.onlyCreatorsBasedInLocation && (
                                    <div className="preview-checkbox-display mt-3">
                                        <span className="checkbox-icon">✓</span>
                                        <span>Only creators based in {formValues.country}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Genre */}
                        {formValues.genres && formValues.genres.length > 0 && (
                            <div className="preview-field mb-4">
                                <label className="preview-label">Genre</label>
                                <div className="preview-tags">
                                    {formValues.genres.map((genreValue, idx) => {
                                        const genreOption = projectConstants.projectGenreOptions.find(
                                            (g) => g.value === genreValue
                                        );
                                        return (
                                            <div key={idx} className="preview-tag-chip">
                                                <span className="tag-text">
                                                    {genreOption ? genreOption.label : formatGenreName(genreValue)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Video URL */}
                        {formValues.videoUrl && (
                            <div className="preview-field mb-4">
                                <label className="preview-label">Video URL</label>
                                <div className="preview-bordered-field">
                                    <p className="preview-value mb-0">{formValues.videoUrl}</p>
                                </div>
                            </div>
                        )}

                        {/* Funding */}
                        {formValues.fundingStatus && (
                            <div className="preview-field mb-4">
                                <label className="preview-label">Funding</label>
                                <div className="preview-bordered-field">
                                    <p className="preview-value mb-0">
                                        {projectConstants.projectFundingStatusOptions.find(
                                            (f) => f.value === formValues.fundingStatus
                                        )?.label ?? formValues.fundingStatus}
                                    </p>
                                </div>
                            </div>
                        )}

                        {isBrand && (
                            <div className="preview-field mb-4">
                                <div className="preview-fee-terms-checkbox">
                                    <label className="preview-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={acceptedFeeTerms}
                                            onChange={(e) => setAcceptedFeeTerms(e.target.checked)}
                                            className="preview-checkbox-input"
                                        />
                                        <span className="preview-checkbox-custom"></span>
                                        <span className="preview-checkbox-text">
                                            I understand MatchHat applies a 10% success-based matching fee only if a creator is successfully matched and accepts the role.
                                        </span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="preview-buttons-section">
                            <button
                                type="button"
                                className="button page-add-project-controls-button has-text-weight-semibold is-outlined"
                                onClick={() => onEdit(2)}
                                disabled={isSubmitting}
                            >
                                Edit
                            </button>
                            <button
                                type="button"
                                className={`button page-add-project-controls-button has-text-weight-semibold is-primary ${isSubmitting ? 'is-loading' : ''
                                    }`}
                                onClick={onSubmit}
                                disabled={isSubmitting || (isBrand && !acceptedFeeTerms)}
                            >
                                Publish
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectPreviewStep;